import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Cpu, Network, GaugeCircle, FlaskConical, Clock, Target, Crosshair, Shield, BookOpen, AlertTriangle, ShieldAlert, Layers, Fuel, Factory, Zap } from "lucide-react";

const moduleMeta = {
  plc_hmi: { label: "PLC + HMI", icon: Cpu },
  scada: { label: "SCADA", icon: Network },
  dcs: { label: "DCS", icon: GaugeCircle },
  lab: { label: "Lab", icon: FlaskConical },
};
const typeMeta = {
  guided: { label: "Guided Lab", icon: BookOpen, color: "text-primary" },
  incident: { label: "Incident Response", icon: AlertTriangle, color: "text-status-warning" },
  red: { label: "Red Team", icon: Crosshair, color: "text-status-critical" },
  blue: { label: "Blue Team", icon: Shield, color: "text-primary" },
};
const diffColor = {
  beginner: "text-status-normal border-status-normal/40 bg-status-normal/10",
  intermediate: "text-status-warning border-status-warning/40 bg-status-warning/10",
  advanced: "text-status-critical border-status-critical/40 bg-status-critical/10",
};

// Plant segregation metadata (order defines section order)
const PLANTS = [
  {
    key: "pipeline",
    name: "Northridge Crude Pipeline",
    vertical: "Oil & Gas Pipeline",
    code: "SITE OG-01",
    blurb: "A 48-mile crude transmission line with inlet/discharge stations, booster pumping, and a surge tank.",
    icon: Fuel,
    accent: "#FF9500",
  },
  {
    key: "manufacturing",
    name: "Meridian Bottling Works",
    vertical: "Discrete Manufacturing",
    code: "SITE MF-02",
    blurb: "A high-speed bottling & packaging line: conveyor, filler, capper, pasteurizer, and a robotic palletizing cell.",
    icon: Factory,
    accent: "#34C759",
  },
  {
    key: "power",
    name: "Cascade Power Generation Station",
    vertical: "Power Generation (DCS)",
    code: "SITE PW-03",
    blurb: "A combined-cycle steam turbine generating unit: boiler drum, steam turbine, synchronous generator, and grid interface under DCS control.",
    icon: Zap,
    accent: "#FFD60A",
  },
];

export default function Scenarios() {
  const [scenarios, setScenarios] = useState([]);
  const [filter, setFilter] = useState("all");
  const [starting, setStarting] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/scenarios").then((r) => setScenarios(r.data)).catch(() => toast.error("Failed to load scenarios"));
  }, []);

  const start = async (id) => {
    setStarting(id);
    try {
      const r = await api.post("/attempts", { scenario_id: id });
      navigate(`/simulator/${r.data.attempt_id}`);
    } catch {
      toast.error("Could not start scenario");
      setStarting(null);
    }
  };

  const filters = [
    { k: "all", l: "All Labs" }, { k: "security", l: "Security" }, { k: "operations", l: "Operations" },
  ];
  const matchesFilter = (s) => filter === "all" || (s.category || "operations") === filter;

  const renderCard = (s) => {
    const M = moduleMeta[s.module] || moduleMeta.lab;
    const T = typeMeta[s.type] || typeMeta.guided;
    const security = (s.category || "operations") === "security";
    const mitre = s.mitre_techniques || [];
    const iec = s.iec62443 || [];
    return (
      <div key={s.scenario_id} data-testid="scenario-card" className="bg-card border rounded-md p-5 flex flex-col transition-colors hover:border-primary/40"
        style={{ borderColor: security ? "rgba(255,59,48,0.25)" : undefined }}>
        <div className="flex items-center justify-between">
          <span className={`flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-sm border ${security ? "text-status-critical border-status-critical/40 bg-status-critical/10" : "text-primary border-primary/40 bg-primary/10"}`}>
            {security ? <ShieldAlert className="w-3 h-3" /> : <FlaskConical className="w-3 h-3" />}{security ? "Security" : "Operations"}
          </span>
          <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-sm border ${diffColor[s.difficulty]}`}>{s.difficulty}</span>
        </div>
        <div className={`flex items-center gap-1.5 text-xs font-medium mt-3 ${T.color}`}><T.icon className="w-3.5 h-3.5" />{T.label}</div>
        <h3 className="font-heading text-lg font-semibold mt-1">{s.title}</h3>
        <p className="text-sm text-muted-foreground mt-1.5 flex-1">{s.description}</p>

        {security && mitre.length > 0 && (
          <div className="mt-3">
            <div className="flex items-center gap-1.5 label-ot text-status-critical mb-1.5"><Layers className="w-3 h-3" />ATT&amp;CK for ICS</div>
            <div className="flex flex-wrap gap-1">
              {mitre.slice(0, 4).map((t) => (
                <span key={t.id} className="font-mono text-[10px] px-1.5 py-0.5 rounded-sm bg-status-critical/10 text-status-critical border border-status-critical/30">{t.id}</span>
              ))}
            </div>
            {iec.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1.5">
                {iec.map((f) => (
                  <span key={f.fr} className="font-mono text-[10px] px-1.5 py-0.5 rounded-sm bg-primary/10 text-primary border border-primary/30">{f.fr}{f.sl != null ? ` · SL${f.sl}` : ""}</span>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center gap-4 mt-4 text-xs font-mono text-muted-foreground">
          <span className="flex items-center gap-1.5"><M.icon className="w-3.5 h-3.5" />{M.label}</span>
          <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />{s.est_minutes}m</span>
          <span className="flex items-center gap-1.5"><Target className="w-3.5 h-3.5" />{s.objectives.length} goals</span>
        </div>
        <Button data-testid="scenario-launch-button" onClick={() => start(s.scenario_id)} disabled={starting === s.scenario_id}
          className="rounded-sm font-semibold mt-4 w-full">
          {starting === s.scenario_id ? "Launching…" : "Launch Simulator"}
        </Button>
      </div>
    );
  };

  return (
    <Layout>
      <div className="mb-6">
        <div className="label-ot text-primary">Training Library</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">OT Security Labs</h1>
        <p className="text-muted-foreground text-sm mt-2 max-w-2xl">
          Hands-on ICS attack &amp; defense exercises across multiple plants. Each lab maps to MITRE ATT&amp;CK for ICS and IEC-62443, blends analyst decisions with live response, and is fully graded.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mb-8">
        {filters.map((f) => (
          <button
            key={f.k}
            data-testid={`filter-${f.k}`}
            onClick={() => setFilter(f.k)}
            className={`px-3 py-1.5 rounded-sm text-sm font-medium border transition-colors ${
              filter === f.k ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground hover:border-border"
            }`}
          >
            {f.l}
          </button>
        ))}
      </div>

      <div className="space-y-10">
        {PLANTS.map((plant) => {
          const labs = scenarios.filter((s) => (s.plant || "pipeline") === plant.key && matchesFilter(s));
          if (labs.length === 0) return null;
          return (
            <section key={plant.key} data-testid={`plant-section-${plant.key}`}>
              {/* plant banner */}
              <div className="flex items-center gap-4 mb-4 pb-3 border-b border-border/60">
                <div className="w-11 h-11 rounded-md border flex items-center justify-center shrink-0"
                  style={{ borderColor: plant.accent + "66", background: plant.accent + "14" }}>
                  <plant.icon className="w-5 h-5" style={{ color: plant.accent }} />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="font-heading text-xl font-bold tracking-tight">{plant.name}</h2>
                    <span className="font-mono text-[10px] px-1.5 py-0.5 rounded-sm border border-border text-muted-foreground">{plant.code}</span>
                    <span className="font-mono text-[10px] px-1.5 py-0.5 rounded-sm" style={{ background: plant.accent + "1f", color: plant.accent }}>{plant.vertical}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{plant.blurb}</p>
                </div>
                <span className="ml-auto font-mono text-xs text-muted-foreground hidden sm:inline">{labs.length} lab{labs.length > 1 ? "s" : ""}</span>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {labs.map(renderCard)}
              </div>
            </section>
          );
        })}
      </div>
    </Layout>
  );
}
